import RailsApiResponse from '@apptypes/api/RailsApiResponse.ts';
import UserBalances from '@apptypes/api/user/Balances.ts';
import { UseGetBalances } from '@hooks/use-get-balances.tsx';
import { useAuth } from '@hooks/useAuth';
import loadable from '@loadable/component';
import { fetchUserBalance } from '@state/reducers/user';
import { getApi } from '@utils/apiUtils';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  CircleHelp,
  MessageCircleQuestionMark,
  PlusCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import NumberFormat from 'react-number-format';
import { useDispatch } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { cache as SWRCache } from 'swr';
import Link from '../../../../components/Link';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import {
  ComponentSettings,
  PagesName,
  ThemeSettings,
} from '../../../../constants';
import { useRoutePath } from '../../../../hooks/index';
import { useConfig } from '../../../../hooks/useConfig';
import useDesktopWidth from '../../../../hooks/useDesktopWidth';
import { useI18n } from '../../../../hooks/useI18n';
import { sortAscending } from '../../../../utils/index';
import { useSocket } from '../../../websocket-provider.tsx';
import { StyledHeaderUserMenu } from '../styled/StyledHeader';

const { icons: icon } = ThemeSettings!;

const LoadableXtremePush = loadable(
  () => import('../../../../components/XtremePushInbox'),
);

const UserMenuLink = ({
  linkIcon,
  link,
  name,
  setShowDropdown,
  children,
}: {
  linkIcon?: string;
  link: string;
  name: string;
  setShowDropdown: (value: boolean) => void;
  children: any;
}) => {
  const { t } = useI18n();
  return (
    <>
      {children ? (
        <>
          <Accordion.Toggle
            className="user-menu__list-item user-menu__list-item-link--toggler"
            as="li"
            eventKey={name}
          >
            <div className="user-menu__list-wrapper">
              <span className="user-menu__list-item-link">
                {linkIcon && (
                  <i
                    className={clsx(linkIcon, 'user-menu__list-item-icon mr-3')}
                  ></i>
                )}
                {name}
              </span>
              <i className={clsx(icon?.down, 'mr-3')}></i>
            </div>
            <Accordion.Collapse eventKey={name}>
              <>
                {children.map(childLink => (
                  <div className="user-menu__list-item-link">
                    <i
                      className={clsx(
                        'icon-transparent',
                        'user-menu__list-item-icon mr-3',
                      )}
                    ></i>

                    {window.__config__.name === 'rwb' &&
                    childLink.name === 'contact_us_link' ? (
                      <span
                        key={childLink.link}
                        className="user-menu__list-sub-item"
                        onClick={() => {
                          window?.zE('messenger', 'show');
                          window?.zE('messenger', 'open');
                          setShowDropdown(false);
                        }}
                      >
                        {t(childLink.name)}
                      </span>
                    ) : (
                      <Link
                        key={childLink.link}
                        to={childLink.link}
                        className="user-menu__list-sub-item"
                        onClick={() => setShowDropdown(false)}
                        target={childLink.target}
                      >
                        {t(childLink.name)}
                      </Link>
                    )}
                  </div>
                ))}
              </>
            </Accordion.Collapse>
          </Accordion.Toggle>
        </>
      ) : (
        <li className="user-menu__list-item">
          <Link
            to={link}
            onClick={() => setShowDropdown(false)}
            className="user-menu__list-item-link"
          >
            {linkIcon && (
              <i
                className={clsx(linkIcon, 'user-menu__list-item-icon mr-3')}
              ></i>
            )}
            {name}
          </Link>
        </li>
      )}
    </>
  );
};

const TestUserMenu = ({
  linkIcon,
  name,
  setShowDropdown,
}: {
  linkIcon?: string;
  name: string;
  setShowDropdown: (value: boolean) => void;
}) => {
  const { user } = useAuth();
  if (!user.test_user) return null;
  const clearCache = async () => {
    return await getApi<string>(
      `${window.location.origin}/api/cache/flushall`,
      {
        responseText: true,
      },
    ).catch(() => null);
  };

  return (
    <Accordion.Toggle
      className="user-menu__list-item user-menu__list-item-link--toggler"
      as="li"
      eventKey={name}
    >
      <div className="user-menu__list-wrapper">
        <span className="user-menu__list-item-link">
          {linkIcon && (
            <i className={clsx(linkIcon, 'user-menu__list-item-icon mr-3')}></i>
          )}
          {name}
        </span>
        <i className={clsx(icon?.down, 'mr-3')}></i>
      </div>
      <Accordion.Collapse eventKey={name}>
        <div className="user-menu__list-item-link">
          <i
            className={clsx(
              'icon-transparent',
              'user-menu__list-item-icon mr-3',
            )}
          ></i>
          <div
            className="user-menu__list-sub-item"
            onClick={() => {
              clearCache();
              setShowDropdown(false);
            }}
          >
            {'Flush Redis Cache'}
          </div>
        </div>
      </Accordion.Collapse>
    </Accordion.Toggle>
  );
};

const HeaderUserInfo = ({ handleLogout, dropdownClasses, isMobile }) => {
  const { t } = useI18n();
  const { sidebars, header } = useConfig();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useAuth();
  const { connected: socketConnected } = useSocket();
  const history = useHistory();
  const [loggingOut, setLoggingOut] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const depositRoute = useRoutePath(PagesName.DepositPage, true);
  const casinoGameRoute = useRoutePath(PagesName.CasinoInnerPage);
  const tabletWidth = useDesktopWidth(991);
  const { icons: icon } = ThemeSettings!;
  const { needsBurger = true } = ComponentSettings?.header || {};
  const {
    gameIntervalTime = 2000,
    defaultIntervalTime = 5000,
    longPoll = true,
  } = ComponentSettings?.balances || {};

  const [selectedInterval, setSelectedInterval] = useState(defaultIntervalTime);
  UseGetBalances({
    interval: selectedInterval,
    socketConnected,
  });

  useEffect(() => {
    if (!user?.logged_in || socketConnected) {
      return;
    }

    if (casinoGameRoute === location.pathname) {
      setSelectedInterval(gameIntervalTime);
    }

    // cleanup
  }, [
    casinoGameRoute,
    gameIntervalTime,
    location.pathname,
    selectedInterval,
    socketConnected,
    user?.logged_in,
  ]);

  const showUserMenu = isOpen => {
    setShowDropdown(isOpen);
  };
  const onLogoutClick = async () => {
    setLoggingOut(true);
    await handleLogout();
    setLoggingOut(false);
    history.push('/');
    SWRCache.clear();
  };

  return (
    <>
      <div className="d-flex justify-content-end flex-grow-1">
        <StyledHeaderUserMenu
          className={clsx(
            'menu-dropdown',
            !window.__config__.xtremepush && 'ml-auto',
            dropdownClasses,
            'styled-header-user-menu',
          )}
          show={showDropdown}
          onToggle={isOpen => showUserMenu(isOpen)}
        >
          <>
            {user.logged_in && (
              <>
                <Button
                  as={Link}
                  to={depositRoute}
                  variant="primary"
                  className="pr-2 pl-3"
                >
                  {user.balance != null ? (
                    <NumberFormat
                      value={user.balance}
                      thousandSeparator
                      displayType={'text'}
                      prefix={user.currency}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      className="font-weight-bold"
                    />
                  ) : (
                    <LoadingSpinner
                      wrapperClassName="d-inline-flex"
                      show
                      small
                      className="mr-1"
                    />
                  )}
                  <PlusCircle className={'ml-2'} />
                </Button>

                {window.__config__.name === 'rwb' && (
                  <div
                    className={
                      'd-flex flex-column align-content-center justify-content-center mx-lg-2 mx-1'
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={'/assets/images/live-chat.png'}
                      className={''}
                      width={28}
                      height={28}
                      onClick={() => {
                        window?.zE('messenger', 'show');
                        window?.zE('messenger', 'open');
                      }}
                    />
                  </div>
                )}

                {!!window.__config__.xtremepush ? (
                  <LoadableXtremePush className="mx-lg-2 mx-1" />
                ) : (
                  <div className="px-2"></div>
                )}
              </>
            )}
            {tabletWidth ? (
              <>
                {user.logged_in && (
                  <Dropdown.Toggle
                    as={Button}
                    variant="secondary"
                    bsPrefix="menu-toggle"
                    className={` pl-3 pr-1`}
                  >
                    <span className="text-capitalize">
                      {user.first_name ? (
                        `${t('hello')} ${user.first_name}`
                      ) : (
                        <LoadingSpinner show={true} small className="mr-0" />
                      )}
                    </span>
                    <i className={clsx(icon?.account, 'm-1')}></i>
                    <i className={clsx(icon?.down, 'mx-2 mb-1')}></i>
                  </Dropdown.Toggle>
                )}
              </>
            ) : (
              <>
                {(user.logged_in || needsBurger) && (
                  <Dropdown.Toggle as="span" className="mobile-user-menu">
                    <i
                      className={clsx(needsBurger ? icon?.menu : icon?.account)}
                    />
                  </Dropdown.Toggle>
                )}
              </>
            )}
          </>
          <Dropdown.Menu
            className="dropdown-menu user-menu"
            data-display="static"
          >
            <div className="user-menu-wrp">
              {user.logged_in && (
                <div className="pt-2 user-menu__info">
                  <div className="row mx-1 my-3">
                    <div className="col">
                      <span className="user-menu__info-title mb-3">
                        {user.name}
                      </span>
                    </div>
                    <div className="w-100"></div>
                    <div className="col">
                      <Link
                        to={depositRoute}
                        className={clsx(
                          'btn btn-primary btn-lg text-16 font-italic btn-block',
                        )}
                      >
                        {t('deposit_link')}
                      </Link>
                      <div className="w-100"></div>
                      <div className="col">
                        <span className="user-menu__info-login mt-3">
                          Last Login:
                          {dayjs(user.last_login_at).format(
                            ' MM-DD-YYYY hh:mm A',
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <ul className="user-menu__list">
                <Accordion>
                  {!tabletWidth &&
                    needsBurger &&
                    header
                      ?.concat()
                      .sort((a, b) => sortAscending(a.order || 0, b.order || 0))
                      .map(link => {
                        const linkPath =
                          link?.path?.[0] === '/' ? link.path : `/${link.path}`;
                        const isDublicate = sidebars?.[0].some(
                          sidebarLink => sidebarLink.link === linkPath,
                        );
                        if (
                          !link.path ||
                          (user.logged_in &&
                            (isDublicate || linkPath === depositRoute))
                        )
                          return null;
                        return (
                          <UserMenuLink
                            key={link.path}
                            linkIcon={link.icon}
                            link={link.path}
                            name={t(link.name)}
                            children={null}
                            setShowDropdown={showUserMenu}
                          />
                        );
                      })}
                  {sidebars &&
                    user.logged_in &&
                    sidebars[0].map(link => (
                      <UserMenuLink
                        key={`${link.link}-${link.name}`}
                        linkIcon={link.icon}
                        link={link.link}
                        name={t(link.name)}
                        children={link.children ? link.children : null}
                        setShowDropdown={showUserMenu}
                      />
                    ))}
                  {user.logged_in && (
                    <TestUserMenu
                      key={'Test User Menu'}
                      linkIcon={clsx(icon?.close)}
                      name={'Test User Menu'}
                      setShowDropdown={showUserMenu}
                    />
                  )}
                </Accordion>
              </ul>
              {user.logged_in && (
                <ul className="user-menu__list d-flex">
                  <div
                    className="user-menu__list-item-link user-menu__list-item-link--no-divider cursor-pointer"
                    onClick={onLogoutClick}
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className={clsx(
                          icon?.logout,
                          'user-menu__list-item-icon mr-3',
                        )}
                      ></i>
                      {t('logout')}
                    </div>
                  </div>
                  <LoadingSpinner show={loggingOut} small />
                </ul>
              )}
            </div>
          </Dropdown.Menu>
        </StyledHeaderUserMenu>
      </div>
    </>
  );
};
export default HeaderUserInfo;
